import { PROFILE_KNOWLEDGE_CHAPTERS } from '../../../features/profileContract';

const normalizeText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const getDateKey = (date = null) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

const hasPartialProfileOrganization = (receipt = {}) => (
  (receipt.sources || []).some((source) => (
    source.metadata?.careSourceProcessing?.profileExtractionRecovery?.status === 'partial'
  ))
);

const getReceiptSourceIds = (receipt = {}) => [
  receipt.sourceKey,
  ...(receipt.sources || []).flatMap((source) => [source.id, source.path]),
].map(normalizeText).filter(Boolean);

const formatDestinationLabels = (sections = []) => {
  const labels = sections.map((section) => section.label);
  if (labels.length <= 2) return labels.join(', ');
  return `${labels.slice(0, 2).join(', ')} + ${labels.length - 2} more`;
};

export const buildHomeProfileUpdateSummary = (
  receipts = [],
  referenceDate = new Date(),
  personName = 'Profile'
) => {
  const todayReceipts = receipts.filter((receipt) => receipt.dateKey === getDateKey(referenceDate));
  if (!todayReceipts.length) return null;

  const itemsByChapter = new Map(PROFILE_KNOWLEDGE_CHAPTERS.map((chapter) => [
    chapter.chapterId,
    { ...chapter, itemKeys: new Set() },
  ]));
  const uniqueItemKeys = new Set();

  todayReceipts.forEach((receipt) => {
    (receipt.profileSections || []).forEach((section) => {
      const destination = itemsByChapter.get(section.chapterId);
      if (!destination) return;
      (section.items || []).forEach((item) => {
        const itemKey = normalizeText(item).toLowerCase();
        if (!itemKey || uniqueItemKeys.has(itemKey)) return;
        uniqueItemKeys.add(itemKey);
        destination.itemKeys.add(itemKey);
      });
    });
  });

  const destinations = [...itemsByChapter.values()]
    .filter((section) => section.itemKeys.size > 0)
    .map(({ itemKeys, ...section }) => ({ ...section, itemCount: itemKeys.size }));
  const savedDetailCount = uniqueItemKeys.size;
  const pendingTotal = todayReceipts.reduce(
    (total, receipt) => total + Number(receipt.pendingTotal || 0),
    0
  );
  const partial = todayReceipts.some(hasPartialProfileOrganization);
  const sourceIds = [...new Set(todayReceipts.flatMap(getReceiptSourceIds))];

  let title = `${personName}'s profile was updated`;
  if (partial) title = `${personName}'s profile was partially updated`;
  else if (!savedDetailCount && pendingTotal) title = `${personName}'s profile needs review`;

  const details = [];
  if (savedDetailCount) {
    const destinationLabel = formatDestinationLabels(destinations);
    details.push(
      `${savedDetailCount} ${savedDetailCount === 1 ? 'detail' : 'details'} added${destinationLabel ? ` to ${destinationLabel}` : ''}`
    );
  }
  if (partial) details.push('Some details need attention');
  else if (pendingTotal) details.push(`${pendingTotal} ${pendingTotal === 1 ? 'needs' : 'need'} review`);

  const directDestination = destinations.length === 1 && !pendingTotal && !partial
    ? destinations[0]
    : null;

  return {
    id: 'home-profile-update-summary',
    kind: 'profile_update_summary',
    category: 'profile_update_receipt',
    title,
    homeSecondaryLabel: details.join(' · '),
    savedDetailCount,
    pendingTotal,
    partial,
    destinations,
    sourceIds,
    profileViewId: directDestination?.profileViewId || '',
  };
};

export default buildHomeProfileUpdateSummary;
